import React, { useEffect, useState } from 'react';
import './Consulting.css';

const Consulting = () => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const form = e.target;
        const formData = new FormData(form);

        try {
            const response = await fetch('https://api.web3forms.com/submit', {
                method: 'POST',
                body: formData,
            });
            const result = await response.json();

            if (result.success) {
                setIsSubmitted(true);
                form.reset();
            } else {
                alert('Something went wrong. Please try again.');
            }
        } catch (error) {
            alert('Something went wrong. Please try again.');
        }

        setIsSubmitting(false);
    };

    return (
        <div className={`page consulting-page ${isLoaded ? 'loaded' : ''}`}>
            {/* Hero — the mark as source identifier */}
            <section className="consulting-hero">
                <div className="container">
                    <span className="label">Consulting &amp; Advisory Services</span>
                    <h1>Technology that disappears into architecture.<span className="tm">™</span></h1>
                    <p className="consulting-hero-subtitle">
                        Independent technology consulting and advisory services for architects,
                        builders, and owners—planning invisible, future-proof building systems
                        from the earliest design phase.
                    </p>
                </div>
            </section>

            {/* Consulting services description — the legally load-bearing section */}
            <section className="consulting-services-section">
                <div className="container">
                    <span className="label">What We Advise On</span>
                    <h2>Consulting from concept, long before construction begins.</h2>
                    <p className="consulting-intro">
                        Before a single device is specified, BAi Systems provides independent
                        technology consulting and advisory services—helping you understand
                        what's possible, plan the right infrastructure, and make confident
                        decisions early, when they cost the least to get right.
                    </p>

                    <div className="consulting-grid">
                        <div className="consulting-card">
                            <h3>Technology Planning &amp; Strategy</h3>
                            <p>
                                Early-phase advisory on what's possible and what infrastructure
                                a design should accommodate—so the technology serves the
                                architecture, not the other way around.
                            </p>
                        </div>
                        <div className="consulting-card">
                            <h3>Feasibility &amp; Site Assessments</h3>
                            <p>
                                We evaluate existing conditions, constraints, and project goals,
                                then recommend a system approach that fits the building and the
                                budget.
                            </p>
                        </div>
                        <div className="consulting-card">
                            <h3>Design Advisory &amp; Concept Development</h3>
                            <p>
                                We consult with your design team on system concepts and
                                coordination strategy, advising on how building systems should
                                fit within the architecture before construction documents are finalized.
                            </p>
                        </div>
                        <div className="consulting-card">
                            <h3>Budget &amp; Scope Consultation</h3>
                            <p>
                                Honest advisory on realistic budgets and scope before commitments
                                are made—so there are no surprises when the project moves forward.
                            </p>
                        </div>
                        <div className="consulting-card">
                            <h3>Owner &amp; Lifestyle Discovery</h3>
                            <p>
                                Advisory sessions that translate how you actually live into a
                                coherent system plan, focused on experience and intent rather
                                than products.
                            </p>
                        </div>
                        <div className="consulting-card">
                            <h3>Independent, Product-Agnostic Guidance</h3>
                            <p>
                                Vendor-neutral recommendations made in your interest, not a
                                manufacturer's. Our loyalty is to the architecture and the
                                outcome.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Engagement — request a consultation, on the same page */}
            <section className="consulting-engage-section">
                <div className="container">
                    <div className="contact-grid">
                        {/* Info Column */}
                        <div className="contact-info">
                            <span className="label">Request a Consultation</span>
                            <h2>Start an advisory conversation.</h2>
                            <p>
                                Tell us about your project and what you're trying to achieve.
                                We'll schedule a consultation to advise on the right approach.
                            </p>

                            <div className="contact-details">
                                <div className="contact-item">
                                    <span className="contact-label">Email</span>
                                    <a href="mailto:hello@bai-systems.com">hello@bai-systems.com</a>
                                </div>
                                <div className="contact-item">
                                    <span className="contact-label">Phone</span>
                                    <a href="tel:+18014775323">(801) 477-5323</a>
                                </div>
                                <div className="contact-item">
                                    <span className="contact-label">Location</span>
                                    <span>Salt Lake City, UT</span>
                                </div>
                            </div>

                            <p className="response-note">
                                We typically respond within one business day.
                            </p>
                        </div>

                        {/* Form Column */}
                        <div className="contact-form-wrapper">
                            {isSubmitted ? (
                                <div className="form-success">
                                    <h3>Thank you for reaching out.</h3>
                                    <p>We'll be in touch within one business day to schedule your consultation.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit}>
                                    <input type="hidden" name="access_key" value="c917a05a-3d9c-4b80-bdbb-ac2c2c4b0a45" />
                                    <input type="hidden" name="subject" value="New Consulting Inquiry - BAi Systems" />
                                    <input type="hidden" name="from_name" value="BAi Systems Website" />
                                    <input type="checkbox" name="botcheck" style={{ display: 'none' }} />

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="name">Name</label>
                                            <input type="text" name="name" id="name" required />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="email">Email</label>
                                            <input type="email" name="email" id="email" required />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="company">Company</label>
                                            <input type="text" name="company" id="company" />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="role">Your Role</label>
                                            <select name="role" id="role">
                                                <option value="">Select...</option>
                                                <option value="builder">Builder / GC</option>
                                                <option value="architect">Architect / Designer</option>
                                                <option value="homeowner">Homeowner</option>
                                                <option value="integrator">Integrator / Installer</option>
                                                <option value="developer">Developer</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="serviceType">Service Needed</label>
                                        <select name="serviceType" id="serviceType" defaultValue="consulting-advisory">
                                            <option value="consulting-advisory">Consulting / Advisory Engagement</option>
                                            <option value="technology-planning">Technology Planning &amp; Strategy</option>
                                            <option value="feasibility-assessment">Feasibility / Site Assessment</option>
                                            <option value="design-advisory">Design Advisory</option>
                                            <option value="budget-scope">Budget &amp; Scope Consultation</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="message">How can we advise you?</label>
                                        <textarea name="message" id="message" rows="6" required></textarea>
                                    </div>

                                    <button type="submit" className="btn btn-primary btn-full" disabled={isSubmitting}>
                                        {isSubmitting ? 'Sending...' : 'Request Consultation'}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Consulting;
